import React from "react";
import { Link } from "react-router-dom";
import { Target, Heart, Award, Users, ArrowRight, CheckCircle2, GraduationCap } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { stats, siteInfo } from "../data/mock";

const About = () => {
  const values = [
    { icon: Target, title: "Missione", desc: "Formare professionisti competenti attraverso percorsi qualificati, riconosciuti e accessibili a tutti." },
    { icon: Heart, title: "Passione", desc: "Crediamo nella formazione come strumento di crescita personale e trasformazione sociale." },
    { icon: Award, title: "Qualità", desc: "Certificati ISO 9001, garantiamo standard elevati in ogni aspetto della nostra attività." },
    { icon: Users, title: "Comunità", desc: "Costruiamo un ecosistema di apprendimento fatto di studenti, docenti e realtà partner." }
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-brand text-white overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-30"></div>
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"></div>

        <div className="relative max-w-5xl mx-auto px-6 py-24 text-center">
          <Badge className="bg-white/10 text-emerald-100 border-white/20 mb-6 backdrop-blur-sm">Chi Siamo</Badge>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight mb-6">
            Costruiamo <span className="italic text-emerald-300">futuro</span> insieme a te
          </h1>
          <p className="text-xl text-emerald-100/90 max-w-3xl mx-auto">
            Da oltre 15 anni, Associazione Progetto Futuro forma professionisti qualificati nel settore socio-sanitario, della sicurezza e delle certificazioni informatiche.
          </p>
        </div>

        <svg className="w-full h-16 -mb-1" viewBox="0 0 1440 60" preserveAspectRatio="none">
          <path d="M0,60 C480,10 960,10 1440,60 L1440,60 L0,60 Z" fill="#fafaf7" />
        </svg>
      </section>

      {/* Story */}
      <section className="py-24 bg-[#fafaf7]">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-emerald-300 to-emerald-100 rounded-3xl rotate-2"></div>
            <img src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&q=80" alt="La nostra sede" className="relative rounded-3xl w-full aspect-[4/5] object-cover shadow-2xl" />
          </div>

          <div>
            <Badge className="bg-brand-light text-brand hover:bg-brand-light mb-4 border-0">La nostra storia</Badge>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-brand-dark mb-6 leading-tight">
              Un impegno per la <span className="italic text-brand">formazione</span>
            </h2>
            <p className="text-lg text-brand-dark/80 leading-relaxed mb-6">
              Nata dalla passione per la formazione professionale, l'Associazione Progetto Futuro ha come obiettivo la crescita del capitale umano nel territorio calabrese e non solo.
            </p>
            <p className="text-lg text-brand-dark/80 leading-relaxed mb-8">
              Ci occupiamo di corsi accreditati per Operatori Socio Sanitari, Assistenti alla Comunicazione, sicurezza sui luoghi di lavoro (D.Lgs. 81/08), certificazioni EIPASS, ECM per professionisti sanitari e consulenza ISO 9001.
            </p>

            <div className="space-y-3">
              {[
                "Ei-Center EIPASS Accreditato",
                "Convenzione con l'Ospedale di Vibo Valentia",
                "Sistema Gestione Qualità ISO 9001",
                "Ente accreditato per la formazione professionale"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-brand flex-shrink-0" />
                  <span className="text-brand-dark/80">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Badge className="bg-brand-light text-brand hover:bg-brand-light mb-4 border-0">I nostri valori</Badge>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-brand-dark leading-tight">
              Ciò in cui <span className="italic text-brand">crediamo</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div key={i} className="bg-[#fafaf7] rounded-3xl p-8 border border-emerald-100 hover:border-brand hover:-translate-y-1 transition-all">
                <div className="w-14 h-14 rounded-2xl bg-brand text-white flex items-center justify-center mb-5">
                  <v.icon className="w-7 h-7" strokeWidth={2} />
                </div>
                <h3 className="font-display text-2xl font-bold text-brand-dark mb-3">{v.title}</h3>
                <p className="text-brand-dark/70 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-brand-light">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="font-display text-5xl md:text-6xl font-bold text-brand">{s.value}</div>
                <div className="text-sm text-brand-dark/70 mt-2 font-medium uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-20 h-20 rounded-full bg-brand mx-auto mb-6 flex items-center justify-center">
            <GraduationCap className="w-10 h-10 text-white" />
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-brand-dark mb-6 leading-tight">
            Unisciti a <span className="italic text-brand">Progetto Futuro</span>
          </h2>
          <p className="text-lg text-brand-dark/70 mb-10">Scopri i nostri corsi e inizia oggi il tuo percorso professionale.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/#corsi">
              <Button size="lg" className="bg-brand hover:bg-brand-dark text-white rounded-full h-14 px-8 font-bold">Esplora i corsi <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </Link>
            <Link to="/contatti">
              <Button size="lg" variant="outline" className="border-brand text-brand hover:bg-brand-light rounded-full h-14 px-8 font-bold">Contattaci</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
